import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  CalendarClock, Loader2, RefreshCw, Search, UserPlus,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import CollectPaymentPanel from '@/components/reception/CollectPaymentPanel';
import TriageDrawer from '@/components/TriageDrawer';
import GlobalPatientSearch from '@/components/GlobalPatientSearch';
import ReceptionPeriodFilters from './ReceptionPeriodFilters';
import ReceptionAppointmentCard from './ReceptionAppointmentCard';
import ReceptionVisitHistoryDrawer from './ReceptionVisitHistoryDrawer';
import { formatDateIST } from '@/utils/date';

const API = process.env.REACT_APP_BACKEND_URL;
const ax = axios.create({ baseURL: API, withCredentials: true });

export default function ReceptionOnboardingBoard({ brand = '#F15A22', onWalkin }) {
  const [period, setPeriod] = useState('today');
  const [statusFilter, setStatusFilter] = useState('all');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [payFor, setPayFor] = useState(null);
  const [triageFor, setTriageFor] = useState(null);
  const [historyFor, setHistoryFor] = useState(null);
  const [localQ, setLocalQ] = useState('');

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const { data: d } = await ax.get('/api/clinic-ops/todays-appointments', {
        params: { period },
      });
      setData(d);
    } catch (_) {
      setData(null);
    }
    setLoading(false);
  }, [period]);

  useEffect(() => {
    reload();
    const t = setInterval(reload, 30000);
    return () => clearInterval(t);
  }, [reload]);

  const rows = useMemo(() => {
    let list = data?.appointments || [];
    if (statusFilter === 'triage') list = list.filter((a) => a.needs_triage);
    if (statusFilter === 'unpaid') list = list.filter((a) => a.needs_payment);
    if (statusFilter === 'awaiting') {
      list = list.filter((a) => a.status === 'awaiting_consultation');
    }
    const q = localQ.trim().toLowerCase();
    if (q) {
      list = list.filter((a) => {
        const blob = [
          a.patient_number, a.patient_name, a.patient_phone,
          a.appointment_number, a.doctor_name,
        ].join(' ').toLowerCase();
        return blob.includes(q);
      });
    }
    return list;
  }, [data, statusFilter, localQ]);

  return (
    <div data-testid="reception-onboarding-board" className="space-y-5">
      <div
        className="relative overflow-hidden rounded-2xl border border-[#D5DEE8] bg-white p-5 shadow-sm"
        style={{ backgroundImage: 'linear-gradient(135deg, #fff 0%, #E8EEF5 55%, #fff7ed 100%)' }}
      >
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <p className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#F15A22]">Patient onboarding</p>
            <h2 className="font-heading text-2xl sm:text-3xl text-[#071B34] mt-1 flex items-center gap-2">
              <CalendarClock className="h-7 w-7" style={{ color: brand }} />
              Appointments board
            </h2>
            <p className="text-sm text-[#5A6B7C] mt-1">
              {data?.date_from ? formatDateIST(data.date_from) : '—'}
              {data?.date_to && data.date_to !== data.date_from
                ? ` → ${formatDateIST(data.date_to)}` : ''}
              {' · '}{data?.counts?.total ?? 0} visits
              {' · '}{data?.counts?.triage_pending ?? 0} triage
              {' · '}{data?.counts?.unpaid ?? 0} payment due
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button size="sm" variant="outline" onClick={reload} disabled={loading} className="h-9">
              {loading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            </Button>
            {onWalkin ? (
              <Button size="sm" className="h-9 text-white gap-1.5" style={{ background: brand }} onClick={onWalkin}
                data-testid="open-walkin-desk">
                <UserPlus className="h-3.5 w-3.5" /> Register walk-in
              </Button>
            ) : null}
          </div>
        </div>
        <div className="mt-4 grid grid-cols-1 lg:grid-cols-[1fr_280px] gap-3">
          <ReceptionPeriodFilters
            period={period} onPeriod={setPeriod}
            statusFilter={statusFilter} onStatus={setStatusFilter}
            brand={brand}
          />
          <div className="space-y-2">
            <GlobalPatientSearch />
            <div className="relative">
              <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9AADBE]" />
              <Input
                value={localQ}
                onChange={(e) => setLocalQ(e.target.value)}
                placeholder="Filter board by patient no. / name…"
                className="pl-8 h-9 bg-white/90"
                data-testid="board-local-search"
              />
            </div>
          </div>
        </div>
      </div>

      {payFor ? (
        <CollectPaymentPanel
          appointment={payFor}
          onDone={() => { setPayFor(null); reload(); }}
          onCancel={() => setPayFor(null)}
        />
      ) : null}

      {loading && !rows.length ? (
        <div className="py-16 text-center text-[#9AADBE]">
          <Loader2 className="h-6 w-6 animate-spin inline" />
        </div>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-[#D5DEE8] bg-white py-16 text-center text-sm text-[#5A6B7C]">
          No appointments for this period and filter.
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {rows.map((a) => (
            <ReceptionAppointmentCard
              key={`${a.kind}-${a.id}`}
              a={a}
              brand={brand}
              collecting={payFor?.id === a.id}
              onCollect={() => setPayFor(a)}
              onTriage={() => setTriageFor(a)}
              onHistory={() => setHistoryFor(a)}
            />
          ))}
        </div>
      )}

      <TriageDrawer
        open={Boolean(triageFor)}
        queueEntry={triageFor}
        onClose={() => setTriageFor(null)}
        onSaved={() => { setTriageFor(null); reload(); }}
      />
      <ReceptionVisitHistoryDrawer
        open={Boolean(historyFor)}
        patient={historyFor ? {
          id: historyFor.patient_id,
          name: historyFor.patient_name,
        } : null}
        excludeId={historyFor?.kind === 'queue' ? historyFor.id : null}
        brand={brand}
        onClose={() => setHistoryFor(null)}
      />
    </div>
  );
}
